import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { 
  getUsageStats, 
  getUserSubscriptionTier, 
  getSubscriptionLimits,
  calculateUsagePercentage,
  getUsageStatusMessage,
  getUpgradeMessage,
  clearUsageCache,
  isUnlimitedPlan
} from '../../utils/mediaUsageUtils';
import './UsageDisplay.css';

const UsageDisplay = ({ 
  showTitle = true, 
  showUpgradeButton = true, 
  compact = false,
  mediaType = "picture" 
}) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [usageData, setUsageData] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState("free");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsageData();
  }, [user]);

  const fetchUsageData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get subscription tier and usage stats in parallel
      const [tier, stats] = await Promise.all([
        getUserSubscriptionTier(user),
        getUsageStats(user)
      ]);

      setSubscriptionTier(tier);

      if (stats.success) {
        setUsageData(stats.data);
      } else {
        setError(stats.error || 'Failed to load usage data');
      }
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    clearUsageCache();
    fetchUsageData();
  };

  const handleUpgrade = () => {
    // Navigate to subscription management
    window.location.href = '/subscription-management';
  };

  if (!user) {
    return (
      <div className={`usage-display ${compact ? 'compact' : ''}`}>
        <p className="usage-message login-required">
          Please log in to view usage information
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`usage-display ${compact ? 'compact' : ''}`}>
        <div className="usage-loading">
          <div className="loading-spinner"></div>
          <span>Loading usage...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`usage-display ${compact ? 'compact' : ''}`}>
        <div className="usage-error">
          <p>Error loading usage data</p>
          <button onClick={handleRefresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!usageData) {
    return (
      <div className={`usage-display ${compact ? 'compact' : ''}`}>
        <p className="usage-message">No usage data available</p>
      </div>
    );
  }

  const limits = getSubscriptionLimits(subscriptionTier);
  const currentCount = mediaType === "picture" ? 
    (usageData.picture_count || 0) : 
    (usageData.video_count || 0);
  const limit = mediaType === "picture" ? 
    limits.monthly_pictures : 
    limits.monthly_videos;
  
  const unlimited = isUnlimitedPlan(limit);
  const remaining = unlimited ? "∞" : Math.max(0, limit - currentCount);
  const percentage = unlimited ? 0 : calculateUsagePercentage(currentCount, limit);
  const canRequest = unlimited || remaining > 0;

  const statusInfo = getUsageStatusMessage(
    { current_count: currentCount, limit, can_request: canRequest, subscription_tier: subscriptionTier },
    mediaType
  );

  const upgradeInfo = !canRequest ? getUpgradeMessage(subscriptionTier, mediaType) : null;

  return (
    <div className={`usage-display ${compact ? 'compact' : ''}`}>
      {showTitle && !compact && (
        <div className="usage-header">
          <h3>Media Usage</h3>
          <button onClick={handleRefresh} className="refresh-button" title="Refresh usage data">
            ↻
          </button>
        </div>
      )}
      
      <div className="usage-content">
        <div className="usage-summary">
          <div className="usage-type-header">
            <span className="media-type-icon">
              {mediaType === "picture" ? "📸" : "🎥"}
            </span>
            <span className="media-type-label">
              {mediaType === "picture" ? "Pictures" : "Videos"}
            </span>
            <span className="plan-badge">{limits.name}</span>
          </div>
          
          <div className="usage-stats">
            <div className="usage-count">
              <span className="current">{currentCount}</span>
              <span className="separator">/</span>
              <span className="limit">{unlimited ? "∞" : limit}</span>
              <span className="unit">used this month</span>
            </div>
            
            <div className="usage-progress">
              <div className="progress-bar">
                <div 
                  className={`progress-fill ${statusInfo.severity}`}
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <span className="progress-text">{percentage}%</span>
            </div>
          </div>
          
          <div className={`usage-message ${statusInfo.severity}`}>
            {statusInfo.message}
          </div>
        </div>

        {upgradeInfo && showUpgradeButton && (
          <div className="upgrade-section">
            <p className="upgrade-message">{upgradeInfo.message}</p>
            <button onClick={handleUpgrade} className="upgrade-button">
              {upgradeInfo.buttonText}
            </button>
          </div>
        )}
        
        {!compact && (
          <div className="usage-details">
            <div className="detail-item">
              <span className="detail-label">Plan:</span>
              <span className="detail-value">{limits.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Remaining:</span>
              <span className="detail-value">
                {unlimited ? "Unlimited" : `${remaining} ${mediaType}${remaining !== 1 ? 's' : ''}`}
              </span>
            </div>
            {usageData.last_picture_request && mediaType === "picture" && (
              <div className="detail-item">
                <span className="detail-label">Last request:</span>
                <span className="detail-value">
                  {new Date(usageData.last_picture_request).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageDisplay;